/**
 * _app/settings layout — two-pane island.
 *
 * The layout owns the <Island> — child routes render their body
 * into the `<Outlet />` inside `<SettingsShell>`, NOT a new Island.
 * The breadcrumb is computed dynamically from the current pathname
 * using topology data so deep URLs like
 * `/app/settings/logs/ss_abc` show `Settings › Developer › Logs ›
 * ss_abc`.
 */

import type { BreadcrumbEntry } from '@/components/shell/Breadcrumb';
import { Island } from '@/components/shell/Island';
import { SettingsShell } from '@/components/shell/SettingsShell';
import { topology } from '@/lib/topology';
import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
});

function buildBreadcrumb(pathname: string): BreadcrumbEntry[] {
  // Walk topology.settings looking for the entry whose href prefix
  // matches. `logs/:auditId` matches the "Logs" entry (prefix match).
  for (const section of topology.settings) {
    for (const item of section.items) {
      if (pathname === item.href || pathname.startsWith(`${item.href}/`)) {
        const trail: BreadcrumbEntry[] = [
          { label: 'Settings', href: '/settings' },
          { label: section.header, href: item.href },
          { label: item.label, href: item.href },
        ];
        // If we're on a deeper path (logs/:auditId), append the leaf.
        if (pathname !== item.href) {
          const leaf = pathname.slice(item.href.length + 1);
          // Drop trailing slash.
          const clean = leaf.replace(/\/+$/, '');
          if (clean.length > 0) trail.push({ label: clean });
        }
        return trail;
      }
    }
  }
  // Fallback — bare /app/settings (should redirect to /profile via
  // the index route, but if it briefly renders, don't blow up).
  return [{ label: 'Settings' }];
}

function SettingsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const crumb = buildBreadcrumb(pathname);

  return (
    <Island breadcrumb={crumb}>
      <SettingsShell>
        <Outlet />
      </SettingsShell>
    </Island>
  );
}
